import React from 'react'
import moment from 'moment';
import { 
  FiEdit2, 
  FiTrash2, 
  FiToggleLeft, 
  FiToggleRight,
  FiCheckCircle,
  FiXCircle,
  FiMoreVertical,
  FiEye,
  FiEyeOff,
  FiClock,
  FiSettings,
  FiPlus,
  FiRefreshCw
} from "react-icons/fi";
import { MdOutlineVerified, MdOutlineBlock, MdOutlineDelete, MdOutlineEdit } from "react-icons/md";
import { BsThreeDotsVertical } from 'react-icons/bs';
import SortableHeader from "../../../components/SortableHeader";

const AppSettingTable = ({ 
  thead, 
  tableName, 
  data, 
  showMenu, 
  isMenu, 
  clickedUserId, 
  onActionClick, 
  size, 
  total, 
  handlePageClick,
  currentSortKey,
  currentSortOrder,
  onSort
}) => {
  // Safe data access - ensure data is always an array
  const settingsData = Array.isArray(data) ? data : [];
  const headerData = Array.isArray(thead) ? thead : [];

  const getStatusClass = (status) => {
    switch (status) {
      case true:
        return 'bg-rose-50 text-rose-700 border border-rose-200';
      case false:
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      default:
        return 'bg-gray-50 text-gray-600 border border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    if (status === true) {
      return <FiXCircle className="w-3.5 h-3.5 text-rose-500" />;
    }
    return <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500" />;
  };

  const getStatusText = (status) => {
    return status === true ? 'Inactive' : 'Active';
  };

  // If no data is available, show empty state
  const isEmpty = settingsData.length === 0;

  return (
    <div className="w-full">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed text-left">
          <thead>
            <tr className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              {headerData.map((heading, index) => {
                if (heading.label === '') return null; // Skip empty label
                
                const headerIcons = {
                  '#': null,
                  'Id': FiSettings, // ID icon used below
                  'Name': FiSettings,
                  'Status': FiToggleLeft,
                  'Action': null
                };
                
                const Icon = headerIcons[heading.label];
                
                return (
                  <th key={index} className={`px-3 md:px-4 py-3 whitespace-nowrap ${heading.label === '#' || heading.label === 'Status' || heading.label === 'Action' ? 'text-center' : ''}`}>
                    <SortableHeader
                      label={heading.label}
                      sortKey={heading.sortKey}
                      currentSortKey={currentSortKey}
                      currentSortOrder={currentSortOrder}
                      onSort={onSort}
                      icon={Icon}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {isEmpty ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="p-4 bg-gray-50 rounded-full">
                      <FiSettings className="w-10 h-10 text-gray-300" />
                    </div>
                    <p className="text-sm font-medium text-gray-500">No settings found</p>
                    <p className="text-xs text-gray-400">Try adjusting your search or filters</p>
                  </div>
                </td>
              </tr>
            ) : (
              settingsData.map((ele, index) => (
                <tr 
                  key={ele?._id || index} 
                  className="hover:bg-gray-50/70 transition-colors duration-150 group"
                >
                  <td className="px-3 md:px-4 py-3 text-center">
                    <span className="text-sm font-medium text-gray-400">
                      {index + 1}
                    </span>
                  </td>
                  <td className="px-3 md:px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                        <FiSettings className="w-3.5 h-3.5 text-[#715EFE]" />
                      </div>
                      <span className="text-xs font-mono text-gray-500 truncate">
                        {ele?._id?.slice(-6) || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 md:px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate font-inter">
                        {ele?.name || 'Unnamed Setting'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        Key: {ele?.key || 'N/A'}
                      </p>
                    </div>
                  </td>
                  <td className="px-3 md:px-4 py-3 text-center">
                    <span className={`
                      inline-flex items-center justify-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full
                      ${getStatusClass(ele?.isDisable)}
                    `}>
                      {getStatusIcon(ele?.isDisable)}
                      {getStatusText(ele?.isDisable)}
                    </span>
                  </td>
                  <td className="px-3 md:px-4 py-3">
                    <div className="relative flex items-center justify-center">
                      <button
                        onClick={() => showMenu && showMenu(ele?._id)}
                        className="p-1.5 text-gray-400 hover:text-[#715EFE] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                      >
                        <BsThreeDotsVertical className="w-4 h-4" />
                      </button>

                      {isMenu && clickedUserId === ele?._id && (
                        <div className="absolute right-0 z-50 w-48 mt-1 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-200 top-full">
                          <div className="py-1">
                            <button
                              className="flex items-center gap-3 px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-[#725EFE33] hover:text-[#715EFE] transition-colors duration-150"
                              onClick={() => onActionClick && onActionClick('EDIT', ele)}
                            >
                              <FiEdit2 className="w-4 h-4" />
                              <span>Edit</span>
                            </button>
                            <button
                              className="flex items-center gap-3 px-4 py-2.5 w-full text-sm text-gray-700 hover:bg-[#725EFE33] hover:text-[#715EFE] transition-colors duration-150"
                              onClick={() => onActionClick && onActionClick('STATUS', ele)}
                            >
                              {ele?.isDisable ? (
                                <>
                                  <FiCheckCircle className="w-4 h-4 text-emerald-500" />
                                  <span>Activate</span>
                                </>
                              ) : (
                                <>
                                  <FiXCircle className="w-4 h-4 text-rose-500" />
                                  <span>Deactivate</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AppSettingTable